import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { PayrunApproverEdit } from '@xemplo/gp-types';
import { useUpdatePayrunApprovers } from '@xemplo/payrun-query';
import { ToastType, useToast } from '@xemplo/toast';

import { FormFields } from './edit-payrun-approvers.types';

type UseApproverHandlerProps = PayrunApproverEdit & {
  methods: ReturnType<typeof useForm<FormFields>>;
};

export function useApproversFormHandler({
  companyId,
  userId,
  methods,
}: UseApproverHandlerProps) {
  const { addToast } = useToast();
  const { handleSubmit, reset } = methods;

  const onSuccess = useCallback(() => {
    addToast({
      text: `Approver updated`,
      type: ToastType.Confirmation,
    });
    reset();
  }, [addToast, reset]);

  const { mutateAsync } = useUpdatePayrunApprovers({
    onSuccess,
    onError: (error) => {
      const errorMessage = error?.response?.data?.validationErrors?.errors[0].message;
      addToast({
        text: errorMessage ?? 'An unexpected error occurred. Please try again later.',
        type: ToastType.Alert,
        duration: 5000,
      });
    },
  });

  const handleFormSubmit = handleSubmit(async (data: FormFields) => {
    if (!companyId || !userId) return;
    const payload = {
      payrunTypeId: Number(data.payrunType),
      payscheduleIds: data.paySchedules ?? [],
      companyId: companyId,
      userId: userId,
    };

    return await mutateAsync(payload);
  });

  return { handleFormSubmit, handleResetForm: () => reset() };
}
